import { useEffect, useMemo } from 'react';

import { useGetCLAOptions } from 'queries';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CLA_SETTINGS_FLAG, TabProps } from '../types';

import { generalSchema } from '../utils';

import Grid from '@mui/material/Grid';

import withReadModeField, {
  DefaultReadModeComponent
} from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import {
  ControlledRadioGroup,
  ControlledTextField
} from 'components/shared/ControlledFields';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import CLASelect from '../../../../shared/CLASelect/CLASelect';
import ControlledCheckbox from '../../../../shared/ControlledFields/ControlledCheckbox';
import ControlledSelect from '../../../../shared/ControlledFields/ControlledSelect';

const ControlledSelectWithReadMode = withReadModeField(ControlledSelect);
const ControlledTextFieldWithReadMode = withReadModeField(ControlledTextField);
const ControlledNumericFieldWithReadMode = withReadModeField(
  ControlledNumericField
);
const CLASelectWithReadMode = withReadModeField(CLASelect);

const GeneralTab = ({ setCurrentSchema, isEditable }: TabProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'adminPortal.cla' });

  const memoizedVariant = useMemo(() => {
    return isEditable ? 'outlined' : 'standard';
  }, [isEditable]);

  const { data: claOptions } = useGetCLAOptions();

  const { watch, control, setValue } = useFormContext();

  const claId = watch('claId');
  const claCode = watch('code');

  useEffect(() => {
    setCurrentSchema(generalSchema(t));
  }, [claId]);

  return (
    <div style={{ paddingBottom: 60 }}>
      <MultiStepForm.Section title={t('general')}>
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 8, md: 12 }}
        >
          <Grid item xs={2} sm={4} md={4}>
            <CLASelectWithReadMode
              disabled={isEditable}
              label={t('CLACode')}
              name="claId"
              readModeRenderer={(props) => (
                <DefaultReadModeComponent {...props} value={claCode ?? ''} />
              )}
              onChange={(claId: string | number) => {
                setValue(
                  'description',
                  claOptions?.find((option) => option.claId === claId)?.code ??
                    ''
                );
              }}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledSelectWithReadMode
              control={control}
              disabled={isEditable}
              name="claId"
              variant="outlined"
              label={t('code')}
              options={
                claOptions?.map((item) => ({
                  label: item.code,
                  value: item.claId
                })) ?? []
              }
              labelGetter="label"
              valueGetter="value"
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledTextFieldWithReadMode
              disabled={isEditable}
              control={control}
              name="claExternalCode"
              variant="outlined"
              label={t('externalCode')}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledTextFieldWithReadMode
              disabled={isEditable}
              control={control}
              name="description"
              label={t('description')}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              name="year"
              variant="outlined"
              label={t('year')}
              step={1}
              minValue={0}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('yearHoursSettings')}>
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 8, md: 12 }}
        >
          <Grid item xs={2} sm={4} md={4}>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name="workHoursBasedWeekdays"
              label={t('workHoursBasedWeekdays')}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={`flags[${CLA_SETTINGS_FLAG.LEAVE_HOURS_EQUAL_PERIODS}]`}
              label={t('leaveEqualPeriods')}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursWorkingPerYear"
              variant={memoizedVariant}
              label={t('yearHours')}
              step={0.2}
              minValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="periodHours"
              variant={memoizedVariant}
              label={t('periodHours')}
              step={0.2}
              minValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursWorkingPerWeek"
              variant={memoizedVariant}
              label={t('weekHours')}
              step={0.2}
              minValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursWorkingPerDay"
              variant={memoizedVariant}
              label={t('dayHours')}
              step={0.2}
              minValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursHolidayPerYear"
              variant={memoizedVariant}
              label={t('leave')}
              step={0.2}
              minValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursPlbYear"
              variant={memoizedVariant}
              label={t('hoursPlbYear')}
              step={0.2}
              minValue={0}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledNumericFieldWithReadMode
              disabled={!isEditable}
              name="hoursHolidayYearMinimum"
              variant={memoizedVariant}
              label={t('minLeave')}
              step={0.2}
              minValue={0}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('PLBSettings')}>
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 8, md: 12 }}
        >
          <Grid item xs={2} sm={4} md={4}>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={`flags[${CLA_SETTINGS_FLAG.PLB_VISIBILITY}]`}
              label={t('PLBHourManagement')}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledTextFieldWithReadMode
              disabled={!isEditable}
              control={control}
              name="plbName"
              label={t('PLBName')}
              variant={memoizedVariant}
            />
          </Grid>
          <Grid item xs={2} sm={4} md={4}>
            <ControlledRadioGroup
              disabled={!isEditable}
              direction="row"
              control={control}
              name="plbValidity"
              label={t('validity')}
              options={[
                { value: CLA_SETTINGS_FLAG.PLB_5_YEARS, label: t('5Year') },
                { value: CLA_SETTINGS_FLAG.PLB_10_YEARS, label: t('10Year') }
              ]}
            />
          </Grid>
        </Grid>
      </MultiStepForm.Section>
    </div>
  );
};

export default GeneralTab;
